/**
 * Pure insight-selection logic for the narrative chart's info notice.
 *
 * Each view is classified into one sentiment — growing / steady / declining —
 * from its primary outcome metric, and the matching observation is shown (see
 * insightMessageCopy.ts). The story is scoped to the period being viewed: single
 * views (This Period / Comparison) describe the displayed line's within-period
 * movement; the Both view compares this period to the comparison period.
 *
 * Free of React (no hooks) so every branch is unit-testable — see
 * selectInsight.test.ts. The hook wrapper (useInsightMessage) only memoizes it.
 */

import type { ErrorLike } from '@apollo/client'

import { formatNumber } from '../../formatNumber'
import { getTimeSeriesSummary } from '../MetricChart/getTimeSeriesSummary'
import { SummaryMode, ViewMode } from '../MetricChart/types'
import type { MetricSeries, TimeSeriesDataPoint } from '../MetricChart/types'
import {
  BENCHMARK_MESSAGES,
  PEAK_MESSAGES,
  postsTotalMessage,
  VIEW_SENTIMENT_MESSAGES,
} from './insightMessageCopy'
import type { NarrativeView } from './narrativeViews'

export interface InsightInput {
  visibleMetrics: MetricSeries[]
  viewMode: ViewMode
  selectedView: NarrativeView | undefined
  singleMetricKey: string
  isBenchmarkView: boolean
  benchmarkChannelRate: number | null
  benchmarkMedianRate: number | null
  loading: boolean
  error: ErrorLike | undefined
  /** No posts were published on the channel in the period. Turns a "held steady"
   *  verdict into the inactive prompt (see {@link InsightResult.variant}). */
  isInactive?: boolean
}

export interface InsightResult {
  message: string | null
  isPositive: boolean
  /** `'inactive'` when the channel had no activity and the metric is flat, so the
   *  notice should point the user to their Takeaways instead of a "held steady"
   *  observation. The copy + Takeaways link are rendered by InsightNotice.
   *  @see CORE-6444 */
  variant?: 'inactive'
}

type Sentiment = 'growing' | 'steady' | 'declining'

const NONE: InsightResult = { message: null, isPositive: false }

/**
 * The metric whose direction drives a view's sentiment. The multi-metric views
 * key off their outcome metric; the Posts tab keys off the selected metric.
 */
const OUTCOME_METRIC_BY_VIEW: Record<string, string> = {
  'content-impact': 'followers',
  'audience-growth': 'followers',
  visibility: 'views',
}

/** The 'posts' tab renders a single user-selected metric rather than a fixed set. */
function isSingleMetricTab(view: NarrativeView): boolean {
  return view.key === 'posts'
}

/** Drops null-valued points so trend maths can assume `value: number`. */
function nonNullPoints(
  data: TimeSeriesDataPoint[],
): Array<{ date: string; value: number }> {
  return data.filter(
    (p): p is { date: string; value: number } => p.value != null,
  )
}

/** The highest non-null point in a series, or null when there's no positive value. */
function peakPoint(
  data: TimeSeriesDataPoint[],
): { date: string; value: number } | null {
  let peak: { date: string; value: number } | null = null
  for (const p of nonNullPoints(data)) {
    if (!peak || p.value > peak.value) peak = p
  }
  return peak && peak.value > 0 ? peak : null
}

/** "2026-06-14" → "Jun 14". Builds a local Date from parts to avoid TZ drift. */
function formatPeakDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Classifies a metric into growing / steady / declining, or `null` when there's
 * no honest verdict for the current view.
 *
 * - Count metrics (Posts, Views, Reach) spike daily and have no within-period
 *   trend, so they're always period-over-period: this period's total vs the
 *   comparison period's, in every view. `null` when there's no comparison data.
 * - Level metrics (Followers, Net New): in the Both view, compare this period's
 *   net change to the comparison period's; in a single view, use the
 *   within-period movement of the displayed line (primary, or `comparisonData`
 *   in Comparison mode).
 */
function classifySentiment(
  metric: MetricSeries,
  viewMode: ViewMode,
): Sentiment | null {
  const isCount = metric.summaryMode === SummaryMode.Total

  if (isCount) {
    if (!metric.comparisonData?.length) return null
    const thisTotal = getTimeSeriesSummary(
      metric.data,
      SummaryMode.Total,
    ).latest
    const lastTotal = getTimeSeriesSummary(
      metric.comparisonData,
      SummaryMode.Total,
    ).latest
    if (thisTotal == null || lastTotal == null) return null
    if (thisTotal > lastTotal) return 'growing'
    if (thisTotal < lastTotal) return 'declining'
    return 'steady'
  }

  if (viewMode === ViewMode.Both) {
    // Level, period-over-period: net change this period vs the comparison.
    if (!metric.comparisonData?.length) return null
    const thisChange = getTimeSeriesSummary(
      metric.data,
      SummaryMode.Latest,
    ).change
    const lastChange = getTimeSeriesSummary(
      metric.comparisonData,
      SummaryMode.Latest,
    ).change
    if (thisChange == null || lastChange == null) return null
    if (thisChange > lastChange) return 'growing'
    if (thisChange < lastChange) return 'declining'
    return 'steady'
  }

  // Level, single view: within-period direction of the displayed line.
  const data =
    viewMode === ViewMode.Comparison && metric.comparisonData?.length
      ? metric.comparisonData
      : metric.data
  const pts = nonNullPoints(data)
  if (pts.length < 2) return 'steady'
  const first = pts[0].value
  const last = pts[pts.length - 1].value
  if (last > first) return 'growing'
  if (last < first) return 'declining'
  return 'steady'
}

/** Benchmark view: the channel's growth rate vs the median for similar accounts. */
export function selectBenchmarkInsight(
  channelRate: number | null,
  medianRate: number | null,
): InsightResult {
  if (channelRate == null || medianRate == null) return NONE

  const isPositive = channelRate >= medianRate
  return {
    message: isPositive ? BENCHMARK_MESSAGES.above : BENCHMARK_MESSAGES.below,
    isPositive,
  }
}

/**
 * Derives the insight message + sentiment for the current narrative view.
 * Pure: same inputs always yield the same result.
 */
export function selectInsight(input: InsightInput): InsightResult {
  const {
    visibleMetrics,
    viewMode,
    selectedView,
    singleMetricKey,
    isBenchmarkView,
    benchmarkChannelRate,
    benchmarkMedianRate,
    loading,
    error,
    isInactive,
  } = input

  if (!selectedView || loading || error) return NONE

  if (isBenchmarkView) {
    return selectBenchmarkInsight(benchmarkChannelRate, benchmarkMedianRate)
  }

  if (!visibleMetrics[0]) return NONE

  // Posts tab → the selected metric; multi-metric views → the view key.
  const messageKey = isSingleMetricTab(selectedView)
    ? singleMetricKey
    : selectedView.key
  const messages = VIEW_SENTIMENT_MESSAGES[messageKey]
  if (!messages) return NONE

  // Sentiment comes from the view's outcome metric (the selected metric on the
  // Posts tab, else the view's mapped outcome), falling back to the first
  // visible series if that metric isn't present.
  const outcomeKey = isSingleMetricTab(selectedView)
    ? singleMetricKey
    : OUTCOME_METRIC_BY_VIEW[selectedView.key]
  const outcomeMetric =
    visibleMetrics.find((m) => m.key === outcomeKey) ?? visibleMetrics[0]

  // Count metrics have no within-period trend, so single views describe the
  // displayed period directly rather than comparing — "…than last period" is
  // meaningless with one line on screen, and circular in the Comparison view.
  // The Both view keeps the comparative copy (handled below).
  if (
    viewMode !== ViewMode.Both &&
    outcomeMetric.summaryMode === SummaryMode.Total
  ) {
    const data =
      viewMode === ViewMode.Comparison && outcomeMetric.comparisonData?.length
        ? outcomeMetric.comparisonData
        : outcomeMetric.data

    // Volume metrics (Profile Views, Reach) → their peak day; Posts → its total.
    const peakTemplate = PEAK_MESSAGES[outcomeMetric.key]
    if (peakTemplate) {
      const peak = peakPoint(data)
      if (!peak) return NONE
      return {
        message: peakTemplate(
          formatNumber(peak.value),
          formatPeakDate(peak.date),
        ),
        isPositive: false,
      }
    }

    if (outcomeMetric.key === 'posts') {
      const total = getTimeSeriesSummary(data, SummaryMode.Total).latest
      if (total == null || total <= 0) return NONE
      return { message: postsTotalMessage(total), isPositive: false }
    }

    // Any other count metric has no single-view story (no peak template, and
    // its volume copy isn't defined here).
    return NONE
  }

  const sentiment = classifySentiment(outcomeMetric, viewMode)
  if (!sentiment) return NONE

  // A flat metric on a channel with no activity reads as cheeky ("held steady").
  // Point the user to their Takeaways instead — InsightNotice renders the copy
  // and the link. `isInactive` reflects the primary period, so skip Comparison
  // view, where the displayed line is the previous period. @see CORE-6444
  if (
    sentiment === 'steady' &&
    isInactive &&
    viewMode !== ViewMode.Comparison
  ) {
    return { message: null, isPositive: false, variant: 'inactive' }
  }

  // Comparative copy for the Both view and for count metrics (which are always
  // period-over-period); within-period "this period" copy for level metrics in
  // single views.
  const isCount = outcomeMetric.summaryMode === SummaryMode.Total
  const set =
    isCount || viewMode === ViewMode.Both
      ? messages.comparison
      : messages.thisPeriod
  if (!set) return NONE

  return {
    message: set[sentiment],
    isPositive: sentiment === 'growing',
  }
}
